import { BaseDataProvider } from './data-provider';
import { type ICollectionParams, type IList, type IResourceParams, type IDataProviderV2, type PartialType } from './models';
import { ItemResource, CollectionResource } from './resource';
import { typedFastCopy } from '../../utils';
import { fetchAllWithPagination } from 'components/system/data-provider/fetch-all-with-pagination';

/**
 * Avoid using excessively large page sizes, as they can strain the backend and increase client memory usage.
 * Change UNLIMITED_AMOUNT with caution.
 */
export const UNLIMITED_AMOUNT = 100;

export abstract class AbstractDataProviderV2<
    ListObject = object,
    InfoObject = object,
    AddObject = object,
    UpdateObject = object,
    PatchObject = object,
    ParamsObject extends IResourceParams = object
>
    extends BaseDataProvider
    implements IDataProviderV2<ListObject, InfoObject, AddObject, UpdateObject, PatchObject, ParamsObject>
{
    public getCollectionResource(resourceParams: Partial<ParamsObject> = {}, debug: boolean = false): CollectionResource<ListObject> {
        return new CollectionResource<ListObject>(this, resourceParams, this.getResourceUrl(resourceParams), debug);
    }

    public getItemResource(resourceParams: Partial<ParamsObject> = {}): ItemResource<InfoObject> {
        return new ItemResource<InfoObject>(this, resourceParams, this.getResourceUrl(resourceParams));
    }

    public list(
        collectionParams: ICollectionParams = {},
        resourceParams: Partial<ParamsObject> = {},
        cancel: Promise<void> = null
    ): Promise<IList<ListObject>> {
        if ((collectionParams?.pagination?.size ?? 0) > UNLIMITED_AMOUNT) {
            console.warn(
                '[dataprovider.list] Page size exceeds recommended maximum.',
                `Requested page size ${collectionParams.pagination?.size ?? 0} exceeds the maximum of ${UNLIMITED_AMOUNT}. 
                This may lead to performance issues. 
                Consider using the 'all' method for large datasets.`
            );
        }
        return this.getTList<ListObject>(this.limitPagination(collectionParams), resourceParams, cancel);
    }

    public all(
        collectionParams: ICollectionParams = {},
        resourceParams: Partial<ParamsObject> = {},
        timeout: Promise<void> = null,
        pageSize: number = UNLIMITED_AMOUNT
    ): Promise<ListObject[]> {
        const params = collectionParams ? typedFastCopy(collectionParams) : {};
        params.pagination = params.pagination || { number: 0, size: pageSize };
        params.pagination.size = Math.min(pageSize, UNLIMITED_AMOUNT);

        // Fetch one page of results (used by the pagination loop in fetchAllWithPagination)
        const fetchPage = (page: ICollectionParams) => {
            return this.getTList<ListObject>(page, resourceParams, timeout);
        };

        return fetchAllWithPagination<ListObject, ICollectionParams>(fetchPage, params);
    }

    public get(resourceParams: object = {}, params: Partial<ParamsObject> = {}, timeout: Promise<void> = null): Promise<InfoObject> {
        return this.getTItem<InfoObject>(resourceParams, params, timeout);
    }

    public update(
        item: PartialType<UpdateObject>,
        resourceParams: Partial<ParamsObject> = {},
        cancel: Promise<void> = null,
        stealth: boolean = false,
        doPackItem: boolean = true
    ): Promise<InfoObject> {
        return this.updateTItem<PartialType<UpdateObject>, InfoObject>(item, resourceParams, cancel, stealth, doPackItem);
    }

    public patch(
        partialItem: PartialType<PatchObject>,
        resourceParams: Partial<ParamsObject> = {},
        cancel: Promise<void> = null,
        stealth: boolean = false
    ): Promise<InfoObject> {
        return this.originalPatchTItem<PartialType<PatchObject>, InfoObject>(partialItem, resourceParams, cancel, stealth);
    }

    public customPatch(
        partialItem: PartialType<UpdateObject>,
        resourceParams: Partial<ParamsObject> = {},
        cancel: Promise<void> = null,
        stealth: boolean = false
    ): Promise<InfoObject> {
        return this.patchTItem<PartialType<UpdateObject>, InfoObject>(partialItem, resourceParams, cancel, stealth);
    }

    public add(
        item: AddObject,
        resourceParams: Partial<ParamsObject> = {},
        cancel: Promise<void> = null,
        stealth: boolean = false,
        doPackItem: boolean = true
    ): Promise<InfoObject> {
        return this.addTItem<AddObject, InfoObject>(item, resourceParams, cancel, stealth, doPackItem);
    }

    public remove(resourceParams: Partial<ParamsObject> = {}, cancel?: Promise<void>, stealth: boolean = false): Promise<void> {
        return this.removeTItem(resourceParams, cancel, stealth);
    }

    protected limitPagination(collectionParams: ICollectionParams): ICollectionParams {
        const params = collectionParams ? typedFastCopy(collectionParams) : {};
        if (!params.pagination) {
            params.pagination = { number: 0, size: UNLIMITED_AMOUNT };
        } else {
            // this accepts all numbers, including '0', unlike '||' operator
            params.pagination.size = Number.isNaN(parseInt(params.pagination.size.toString(), 10)) ? UNLIMITED_AMOUNT : params.pagination.size;
        }

        return params;
    }

    /**
     * @deprecated
     */
    protected handleList(result: any[]): ListObject[] {
        return result.map(item => this.handleListItem(item));
    }

    /**
     * @deprecated
     */
    protected handleItem(result: any): InfoObject {
        return result as InfoObject;
    }

    /**
     * @deprecated
     */
    protected handleListItem(result: any): ListObject {
        return result as ListObject;
    }

    /**
     * @deprecated
     */
    protected packItem(item: any): UpdateObject {
        return item as UpdateObject;
    }
}
